import React from "react";

/**
 * Top-level error boundary — catches synchronous render errors in any
 * descendant and shows a friendly recovery card with a Reload button.
 *
 * Reason for existing: before this component was added, any uncaught
 * exception during initial mount (e.g. a bad axios interceptor response,
 * a bad chunk load, or a runtime error inside a Provider) would leave the
 * screen completely blank white — indistinguishable from a still-loading
 * state. That was the failure mode users saw during the Industry Profiles
 * regression.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Log so we can pick it up in browser devtools / posthog session replay.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info?.componentStack);
  }

  handleReload = () => {
    // Full reload wipes any transient in-memory state that might have
    // caused the crash. Using replace() means the current URL stays put.
    try { window.location.reload(); } catch { /* ignore */ }
  };

  /**
   * Send the user "home" — role-aware.
   *
   * ErrorBoundary lives above AuthProvider in the tree (it must, so that
   * a crash inside AuthProvider itself still renders the recovery card),
   * so we can't use `useAuth()`. Instead, we read the same access token
   * the app persists to `localStorage` / `sessionStorage`, decode the
   * JWT payload synchronously, and pick the correct dashboard for the
   * user's role. Guests fall through to the public landing page.
   *
   * The JWT is only trusted for a lightweight routing hint here — if it's
   * expired/tampered, the destination route's <RequireAuth> guard will
   * simply bounce the user back to /login. No security decision is made
   * from this decode.
   */
  handleHome = () => {
    let target = "/";
    try {
      const raw =
        (typeof window !== "undefined" &&
          (window.localStorage.getItem("tt_access_token") ||
            window.sessionStorage.getItem("tt_access_token"))) ||
        null;
      if (raw && typeof raw === "string" && raw.split(".").length === 3) {
        const payload = JSON.parse(
          decodeURIComponent(
            atob(raw.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))
              .split("")
              .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
              .join(""),
          ),
        );
        const notExpired = payload?.exp ? payload.exp * 1000 > Date.now() : true;
        if (notExpired) {
          if (payload?.role === "coach") target = "/coach/dashboard";
          else if (payload?.role === "parent") target = "/parent/dashboard";
          else if (payload?.role === "platform_admin") target = "/admin/dashboard";
        }
      }
    } catch {
      // Any parsing failure → fall through to the public landing page.
    }
    try { window.location.replace(target); } catch { /* ignore */ }
  };

  render() {
    if (!this.state.error) return this.props.children;
    const msg = (this.state.error && (this.state.error.message || String(this.state.error))) || "Unexpected error";
    return (
      <div
        role="alert"
        data-testid="app-error-boundary"
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
          background: "#F5F6F8",
          fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif",
          color: "#1A1A1A",
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: 480,
            background: "#ffffff",
            borderRadius: 12,
            padding: 32,
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
            border: "1px solid #E5E7EB",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700, fontSize: 18, marginBottom: 12 }}>
            <span
              style={{
                display: "inline-block",
                width: 28,
                height: 28,
                background: "#005CFF",
                borderRadius: 6,
                color: "#fff",
                textAlign: "center",
                lineHeight: "28px",
              }}
            >S</span>
            Squad Fee
          </div>
          <h1 style={{ fontSize: 22, margin: "0 0 8px", fontWeight: 700 }}>
            Something went wrong
          </h1>
          <p style={{ margin: "0 0 20px", color: "#4B5563", fontSize: 14, lineHeight: 1.5 }}>
            The app hit an unexpected error while loading. Try reloading — if it keeps
            happening, please reach out to support and share the details below.
          </p>
          <pre
            data-testid="app-error-details"
            style={{
              background: "#F9FAFB",
              border: "1px solid #E5E7EB",
              borderRadius: 8,
              padding: 12,
              fontSize: 12,
              color: "#6B7280",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              margin: "0 0 20px",
              maxHeight: 160,
              overflow: "auto",
            }}
          >
            {msg}
          </pre>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              data-testid="app-error-reload"
              onClick={this.handleReload}
              style={{
                background: "#005CFF",
                color: "#ffffff",
                border: "none",
                borderRadius: 8,
                padding: "12px 20px",
                fontWeight: 600,
                fontSize: 14,
                cursor: "pointer",
                flex: 1,
              }}
            >
              Reload page
            </button>
            <button
              data-testid="app-error-home"
              onClick={this.handleHome}
              style={{
                background: "#ffffff",
                color: "#1A1A1A",
                border: "1px solid #E5E7EB",
                borderRadius: 8,
                padding: "12px 20px",
                fontWeight: 600,
                fontSize: 14,
                cursor: "pointer",
              }}
            >
              Go home
            </button>
          </div>
        </div>
      </div>
    );
  }
}
